import type { Couleur } from '@taki/data-model';
import type { Option, ProjectedRow } from '../contract.js';
import { sommer } from '../champs-types.js';
import { compareCells, sortValueOf } from '../project.js';
import { repartirEnEtapes } from './etapes.js';
import type { EtapeRepartie } from './etapes.js';

/**
 * LES CALCULS DU TABLEAU DE BORD (`core.bord`, `docs/socle/tableau-de-bord.md`),
 * PURS : des lignes deja projetees et des blocs deja compiles en entree, des
 * nombres et des listes en sortie. Aucun React, aucun enregistrement brut : la
 * vue compte et additionne CE QUE LA PORTE MONTRE (recherche et filtres compris).
 *
 * LES BLOCS ARRIVENT COMPILES (`bindingsDe`, `views/screens.ts`) : chaque
 * condition `si` et chaque champ additionne ou trie est un ROLE PROPRE de la
 * ligne, donc une clef de `row.extra`. Le calcul ne connait que ces clefs :
 * - `bord.si.<bloc>.<rang>` : le OUI / NON de la condition d'un chiffre ;
 * - `bord.si.<bloc>` : celui de la condition d'une liste ;
 * - `bord.val.<bloc>.<rang>` : le nombre additionne par un chiffre ;
 * - `bord.tri.<bloc>.<rang>` : la valeur comparable d'un niveau de tri par champ.
 */

/** Un chiffre cle compile. */
export interface ChiffreDeBord {
  libelle: string;
  calcul: 'compte' | 'somme';
  /** La clef de `row.extra` du OUI / NON de la condition. Absente : toutes les fiches. */
  si?: string;
  /** La clef de `row.extra` du nombre additionne (`somme` seulement). */
  val?: string;
  couleur?: Couleur;
  /** L'habillage de la somme : celui du pied de colonne du champ (unite, decimales). */
  format?: (valeur: number) => string;
  /** Additionner au centime (un montant) : mille lignes ne derivent pas d'un centime. */
  auCentime?: boolean;
}

/** Un niveau de tri compile : un role de premiere classe, ou une clef de `row.extra`. */
export interface NiveauDeTriDeBord {
  cle: string;
  /** Vrai : `cle` est un role que `sortValueOf` lit sur la ligne (`title`, `dateStart`...). */
  role: boolean;
  sens: 'asc' | 'desc';
}

/** Un bloc compile, dans l'ordre ou il se lit. */
export type BlocDeBord =
  | { type: 'chiffres'; libelle?: string; chiffres: ChiffreDeBord[] }
  | { type: 'etapes'; libelle?: string; repliees: string[] }
  | { type: 'liste'; libelle: string; si?: string; tri: NiveauDeTriDeBord[]; plafond: number };

/** Ce que `bindingsDe` pose dans `binding.options.bord`. */
export interface BordCompile {
  blocs: BlocDeBord[];
}

/** Le plafond d'une liste qui n'en declare pas. */
export const PLAFOND_DE_LA_LISTE = 5;

/** Sans `blocs` : un chiffre « Total », puis les etapes si `groupKey` est declare. */
export function blocsParDefaut(avecEtapes: boolean): BlocDeBord[] {
  return [
    { type: 'chiffres', chiffres: [{ libelle: 'Total', calcul: 'compte' }] },
    ...(avecEtapes ? [{ type: 'etapes' as const, repliees: [] }] : []),
  ];
}

/** Les blocs a montrer : ceux du binding, sinon le tableau de bord par defaut. */
export function blocsDuBord(options: Record<string, unknown>, avecEtapes: boolean): BlocDeBord[] {
  const bord = options['bord'] as Partial<BordCompile> | undefined;
  return Array.isArray(bord?.blocs) && bord.blocs.length > 0 ? bord.blocs : blocsParDefaut(avecEtapes);
}

/** La ligne passe-t-elle la condition portee par cette clef ? Sans clef : oui. */
const passe = (row: ProjectedRow, clef: string | undefined): boolean =>
  clef === undefined || row.extra[clef] === true;

/**
 * LA VALEUR D'UN CHIFFRE : le compte des fiches qui passent `si`, ou la somme du
 * champ sur ces fiches. Une fiche sans nombre n'ajoute rien (ni zero ni refus).
 */
export function calculerChiffre(rows: readonly ProjectedRow[], chiffre: ChiffreDeBord): number {
  const retenues = rows.filter((row) => passe(row, chiffre.si));
  if (chiffre.calcul === 'compte' || chiffre.val === undefined) return retenues.length;
  const nombres: number[] = [];
  for (const row of retenues) {
    const valeur = row.extra[chiffre.val];
    if (typeof valeur === 'number' && Number.isFinite(valeur)) nombres.push(valeur);
  }
  return sommer(nombres, chiffre.auCentime === true);
}

/** La valeur comparable d'une ligne pour un niveau de tri. */
const valeurDeTri = (row: ProjectedRow, niveau: NiveauDeTriDeBord) =>
  niveau.role ? sortValueOf(row, niveau.cle) : row.extra[niveau.cle];

const estVide = (valeur: unknown): boolean => valeur === null || valeur === undefined || valeur === '';

/**
 * Compare deux lignes sur une pile de niveaux. LES VIDES FINISSENT TOUJOURS EN
 * BAS, quel que soit le sens (comme le tri des vues) : une fiche sans echeance
 * ne remonte pas en tete d'un « A traiter d'abord » parce qu'on trie a l'envers.
 */
export function comparerLesLignes(a: ProjectedRow, b: ProjectedRow, tri: readonly NiveauDeTriDeBord[]): number {
  for (const niveau of tri) {
    const va = valeurDeTri(a, niveau);
    const vb = valeurDeTri(b, niveau);
    const aVide = estVide(va);
    const bVide = estVide(vb);
    if (aVide && bVide) continue;
    if (aVide) return 1;
    if (bVide) return -1;
    const ecart = compareCells(va, vb);
    if (ecart !== 0) return niveau.sens === 'desc' ? -ecart : ecart;
  }
  return 0;
}

/**
 * « A TRAITER D'ABORD » : les fiches qui passent `si`, triees, coupees au
 * plafond. `reste` dit ce que la coupe laisse de cote, pour que la liste ne
 * passe jamais pour le tout. Sans `tri`, l'ordre de la porte est gardé.
 */
export function listeATraiter(
  rows: readonly ProjectedRow[],
  bloc: Extract<BlocDeBord, { type: 'liste' }>,
): { lignes: ProjectedRow[]; reste: number } {
  const retenues = rows.filter((row) => passe(row, bloc.si));
  // Le tri est stable : a valeurs egales, l'ordre de la porte decide.
  const triees = bloc.tri.length === 0 ? retenues : [...retenues].sort((a, b) => comparerLesLignes(a, b, bloc.tri));
  return { lignes: triees.slice(0, bloc.plafond), reste: Math.max(0, triees.length - bloc.plafond) };
}

/** Une etape du bloc `etapes`, et si elle arrive repliee. */
export interface RangeeDEtape {
  etape: EtapeRepartie;
  repliee: boolean;
}

/**
 * LES RANGEES DU BLOC DES ETAPES : une par valeur du catalogue, dans son ordre,
 * plus le fourre-tout s'il sert (la regle est celle du kanban, `etapes.ts`).
 * Une valeur de `repliees` arrive repliee ; le fourre-tout se replie par `''`.
 */
export function rangeesDEtapes(
  rows: readonly ProjectedRow[],
  catalogue: readonly Option[],
  repliees: readonly string[],
): RangeeDEtape[] {
  return repartirEnEtapes(rows, catalogue).map((etape) => ({
    etape,
    repliee: repliees.includes(etape.option.value),
  }));
}
